/** What Feedback shows a category and a status as, shared by the list and a request's page. */

import type { LucideIcon } from 'lucide-react';
import {
  Ban,
  Bug,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  CircleHelp,
  CalendarClock,
  Lightbulb,
  Loader,
  Sparkles,
  XCircle,
} from 'lucide-react';

import type { FeedbackAdminStatus, FeedbackCategory, FeedbackStatus } from '@/api/types';

export const CATEGORIES: { value: FeedbackCategory; label: string; hint: string; Icon: LucideIcon }[] = [
  { value: 'bug', label: 'Bug', hint: 'Something does not work as it should', Icon: Bug },
  { value: 'enhancement', label: 'Enhancement', hint: 'Make something that exists work better', Icon: Sparkles },
  { value: 'suggestion', label: 'Suggestion', hint: 'An idea for something new', Icon: Lightbulb },
  { value: 'question', label: 'Question', hint: 'Ask how something works', Icon: CircleHelp },
];

export const CATEGORY = Object.fromEntries(CATEGORIES.map((c) => [c.value, c])) as Record<
  FeedbackCategory,
  (typeof CATEGORIES)[number]
>;

export const STATUSES: { value: FeedbackStatus; label: string; className: string; Icon: LucideIcon; hint: string }[] = [
  { value: 'new', label: 'New', className: 'badge-info', Icon: CircleDot, hint: 'Filed; no admin has looked at it yet' },
  { value: 'in_review', label: 'In review', className: 'badge-warning', Icon: CircleDashed, hint: 'An admin is looking into it' },
  { value: 'planned', label: 'Planned', className: 'badge-accent', Icon: CalendarClock, hint: 'Accepted and scheduled' },
  { value: 'in_progress', label: 'In progress', className: 'badge-warning', Icon: Loader, hint: 'Being worked on' },
  { value: 'resolved', label: 'Resolved', className: 'badge-success', Icon: CheckCircle2, hint: 'Done, fixed or answered' },
  { value: 'declined', label: 'Declined', className: 'badge-danger', Icon: XCircle, hint: 'Will not be done' },
  { value: 'cancelled', label: 'Cancelled', className: 'badge-neutral', Icon: Ban, hint: 'Withdrawn by the person who filed it' },
];

export const STATUS = Object.fromEntries(STATUSES.map((s) => [s.value, s])) as Record<FeedbackStatus, (typeof STATUSES)[number]>;

/** What an admin may move a request to; cancelling is the author's own decision. */
export const ADMIN_STATUSES = STATUSES.filter((s) => s.value !== 'cancelled') as ((typeof STATUSES)[number] & {
  value: FeedbackAdminStatus;
})[];

export const isOpen = (status: FeedbackStatus) => ['new', 'in_review', 'planned', 'in_progress'].includes(status);

export function StatusPill({ status }: { status: FeedbackStatus }) {
  const { label, className, Icon } = STATUS[status];
  return (
    <span className={`badge ${className}`}>
      <Icon aria-hidden="true" /> {label}
    </span>
  );
}

export function CategoryLabel({ category }: { category: FeedbackCategory }) {
  const { label, Icon } = CATEGORY[category];
  return (
    <span className="category-label">
      <Icon aria-hidden="true" /> {label}
    </span>
  );
}

/** "3 min ago", "Yesterday", or the date: how long since a request last moved.
 *  `inline` keeps it lowercase for use inside a sentence ("filed just now"). */
export function relativeTime(iso: string, { inline = false }: { inline?: boolean } = {}): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return inline ? 'just now' : 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return inline ? 'yesterday' : 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
